import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import {Button} from '@wandb/weave/components/Button';
import * as _ from 'lodash';
import * as React from 'react';
import {useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Checkbox, Divider, Label, Popup} from 'semantic-ui-react';

import * as QueryTypes from '../../../util/queryTypes';
import * as Run from '../../../util/runs';
import * as RunTypes from '../../../util/runTypes';
import * as UIHelpers from '../../../util/uihelpers';

interface WBTableActionGroupButtonProps {
  compact?: boolean;
  groupOpen: boolean;
  grouping: QueryTypes.Grouping;
  isInReport?: boolean;
}

export const WBTableActionGroupButton = (
  props: WBTableActionGroupButtonProps
) => {
  const groupCount = props.grouping.length;

  const {compact, grouping, groupOpen, isInReport, ...rest} = props;

  const isActive = props.groupOpen || groupCount > 0;
  const buttonText =
    groupCount > 0
      ? 'Grouped by ' + UIHelpers.maybePluralize(groupCount, 'field')
      : 'Group';

  return (
    <span
      onClick={() => {
        const actionLocation = props.isInReport ? 'report' : 'runs table';
        window.analytics?.track('Group dropdown clicked', {
          location: actionLocation,
        });
      }}>
      <Button
        {...rest}
        active={isActive}
        icon="group"
        variant="secondary"
        tooltip={props.compact ? 'Group' : ''}
        tooltipProps={{className: 'py-8 px-12'}}
        data-test="group-button"
        aria-label="Groups">
        {props.compact ? '' : buttonText}
      </Button>
    </span>
  );
};

interface WBTableActionGroupDropdownProps {
  groupableKeys: string[];
  loading: boolean;
  grouping: QueryTypes.Grouping;

  setGrouping(grouping: QueryTypes.Grouping): void;
}

export const WBTableActionGroupDropdown = (
  props: WBTableActionGroupDropdownProps
) => {
  const {groupableKeys, loading, grouping, setGrouping} = props;
  const options = UIHelpers.makeOptions(groupableKeys);
  return (
    <ModifiedDropdown
      style={{minWidth: '205px'}}
      placeholder="Group by..."
      data-test="group-dropdown"
      closeOnBlur
      multiple
      lazyLoad
      selection
      optionTransform={UIHelpers.beautify}
      options={options}
      search={UIHelpers.searchFunction}
      value={grouping.map(v => Run.keyToString(v))}
      disabled={loading}
      loading={loading}
      itemLimit={100}
      closeOnChange
      onChange={(e, {value}) => {
        // We know it's string[] because we pass in options
        const typedValue: string[] = value as string[];
        // We filter out nulls so casting to RunTypes.Key[] is safe
        const newGrouping: RunTypes.Key[] = typedValue
          .map(v => Run.keyFromString(v))
          .filter(o => o != null) as RunTypes.Key[];
        setGrouping(newGrouping);
      }}
    />
  );
};

interface WBTableActionGroupPickerProps {
  grouping: QueryTypes.Grouping;
  recommendedGrouping?: QueryTypes.Grouping; // A good grouping for the current data
  dropdown: JSX.Element;

  setGrouping(grouping: QueryTypes.Grouping): void;
}

export const WBTableActionGroupPicker = (
  props: WBTableActionGroupPickerProps
) => {
  const {recommendedGrouping, grouping, dropdown, setGrouping} = props;

  return (
    <div>
      {dropdown}
      {recommendedGrouping != null && recommendedGrouping.length !== 0 && (
        <React.Fragment>
          <Divider />
          <div className="filter-list__toggle">
            <span style={{marginRight: 16, whiteSpace: 'nowrap'}}>
              Recommended:{' '}
              {recommendedGrouping.map((k, i) => (
                <Label key={i}>
                  {UIHelpers.getImage(k)} {Run.keyDisplayName(k, true)}
                </Label>
              ))}
            </span>
            <Checkbox
              className="filter-list__action"
              toggle
              checked={_.isEqual(grouping, recommendedGrouping)}
              onChange={() => {
                if (_.isEqual(grouping, recommendedGrouping)) {
                  setGrouping([]);
                } else {
                  setGrouping(recommendedGrouping);
                }
              }}
            />
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

interface WBTableActionGroupProps {
  trigger: (isOpen: boolean) => React.ReactNode;
  content: React.ReactNode;
}

export const WBTableActionGroup = (props: WBTableActionGroupProps) => {
  const [open, setOpen] = useState(false);
  return (
    <Popup
      basic
      className="wb-table-action-popup"
      on="click"
      position="bottom left"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      trigger={props.trigger(open)}
      content={props.content}
      popperModifiers={{
        preventOverflow: {enabled: false},
        flip: {enabled: false},
      }}
    />
  );
};
